import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { track, ANALYTICS_EVENTS } from "../../../utils/productAnalytics";

const plans = [
  {
    name: "Personal",
    target: "個人・小規模事業者",
    annual: "9,800",
    monthly: "16,000",
    credits: "10,000",
    users: "1 ID",
    products: "3",
    perUnit: "約¥54",
    volume: "約180枚/月",
    support: "チャット",
    popular: false,
  },
  {
    name: "Team",
    target: "内製化推進企業",
    annual: "30,000",
    monthly: "50,000",
    credits: "35,000",
    users: "無制限",
    products: "3",
    perUnit: "約¥46",
    volume: "約650枚/月",
    support: "専任担当",
    popular: true,
  },
  {
    name: "Agency",
    target: "広告代理店",
    annual: "50,000",
    monthly: "80,000",
    credits: "60,000",
    users: "無制限",
    products: "無制限",
    perUnit: "約¥45",
    volume: "約1,100枚/月",
    support: "専任担当 + 定例",
    popular: false,
  },
];

const costComparison = [
  {
    volume: "月10枚",
    outsource: "50,000〜300,000円",
    tasky: "9,800円",
  },
  {
    volume: "月50枚",
    outsource: "250,000〜1,500,000円",
    tasky: "30,000円",
  },
  {
    volume: "月100枚",
    outsource: "500,000〜3,000,000円",
    tasky: "50,000円",
  },
];

const PricingSection: React.FC = () => {
  const [isAnnual, setIsAnnual] = useState(true);

  return (
    <section id="pricing" className="py-24 md:py-40">
      <div className="max-w-[1200px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center h-7 px-3 rounded-full bg-[#1d1926]/5 text-[#1d1926]/60 text-[13px] font-medium tracking-[0.04em]">
            Pricing
          </span>
          <h2 className="mt-5 text-[36px] md:text-[64px] font-extrabold tracking-[-0.03em]">
            外注1〜2枚分の価格で、
            <br className="hidden md:block" />
            月180枚から。
          </h2>
          <p className="mt-4 text-[18px] text-[#1d1926]/60">
            7日間無料トライアル。クレジットカード不要で開始できます。
          </p>

          {/* Toggle */}
          <div className="mt-8 inline-flex items-center gap-3 p-1 rounded-full bg-white border border-[rgba(29,25,38,0.08)]">
            <button
              onClick={() => setIsAnnual(true)}
              className={`px-5 py-2 rounded-full text-[14px] font-medium transition-all ${
                isAnnual
                  ? "bg-[#1d1926] text-[#f6f5f0]"
                  : "text-[#1d1926]/60 hover:text-[#1d1926]"
              }`}
            >
              年契約
              <span
                className={`ml-1.5 text-[12px] ${
                  isAnnual ? "text-[#f6f5f0]/50" : "text-[#1d1926]/40"
                }`}
              >
                最大38%OFF
              </span>
            </button>
            <button
              onClick={() => setIsAnnual(false)}
              className={`px-5 py-2 rounded-full text-[14px] font-medium transition-all ${
                !isAnnual
                  ? "bg-[#1d1926] text-[#f6f5f0]"
                  : "text-[#1d1926]/60 hover:text-[#1d1926]"
              }`}
            >
              月契約
            </button>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className={`relative p-8 rounded-[20px] border ${
                plan.popular
                  ? "bg-[#1d1926] text-[#f6f5f0] border-[#1d1926]"
                  : "bg-white border-[rgba(29,25,38,0.08)]"
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-3 left-8 inline-flex items-center h-6 px-3 rounded-full bg-white text-[#1d1926] text-[12px] font-semibold">
                  人気
                </span>
              )}

              <p
                className={`text-[14px] font-medium ${
                  plan.popular ? "text-[#f6f5f0]/60" : "text-[#1d1926]/40"
                }`}
              >
                {plan.target}
              </p>
              <h3 className="mt-1 text-[28px] font-bold">{plan.name}</h3>

              <div className="mt-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-[44px] font-extrabold leading-none">
                    ¥{isAnnual ? plan.annual : plan.monthly}
                  </span>
                  <span
                    className={`text-[14px] ${
                      plan.popular ? "text-[#f6f5f0]/50" : "text-[#1d1926]/40"
                    }`}
                  >
                    /月
                  </span>
                </div>
                {isAnnual && (
                  <p
                    className={`mt-1 text-[14px] ${
                      plan.popular ? "text-[#f6f5f0]/40" : "text-[#1d1926]/40"
                    }`}
                  >
                    月契約: ¥{plan.monthly}/月
                  </p>
                )}
              </div>

              <Link
                to="/signup"
                onClick={() =>
                  track(ANALYTICS_EVENTS.CTA_CLICKED, {
                    cta_id: `pricing_signup_${plan.name}`,
                    location: "landing_pricing",
                    billing_cycle: isAnnual ? "annual" : "monthly",
                  })
                }
                className={`mt-6 w-full inline-flex items-center justify-center h-12 rounded-full text-[16px] font-semibold transition-opacity hover:opacity-90 no-underline ${
                  plan.popular
                    ? "bg-[#3eb298] text-[#1d1926]"
                    : "bg-[#1d1926] text-[#f6f5f0]"
                }`}
              >
                無料で試す
              </Link>

              <ul className="mt-8 space-y-3">
                {[
                  { label: "クレジット", value: plan.credits },
                  { label: "ユーザー数", value: plan.users },
                  { label: "商品登録", value: plan.products },
                  { label: "1枚あたり", value: plan.perUnit },
                  { label: "月間生成目安", value: plan.volume },
                  { label: "サポート", value: plan.support },
                ].map((item) => (
                  <li
                    key={item.label}
                    className={`flex justify-between text-[14px] ${
                      plan.popular
                        ? "text-[#f6f5f0]/70 border-[#f6f5f0]/10"
                        : "text-[#1d1926]/60 border-[rgba(29,25,38,0.08)]"
                    } pb-3 border-b last:border-0 last:pb-0`}
                  >
                    <span>{item.label}</span>
                    <span
                      className={`font-medium ${
                        plan.popular ? "text-[#f6f5f0]" : "text-[#1d1926]"
                      }`}
                    >
                      {item.value}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Enterprise callout */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-8 p-8 rounded-[20px] bg-white border border-[rgba(29,25,38,0.08)] flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
        >
          <div>
            <h3 className="text-[28px] font-bold">Enterprise</h3>
            <p className="mt-1 text-[16px] text-[#1d1926]/60">
              大規模導入・カスタム要件に対応。専任担当 + 定例ミーティング付き。
            </p>
          </div>
          <a
            href="#cta"
            className="inline-flex items-center h-12 px-8 rounded-full border border-[rgba(29,25,38,0.14)] text-[#1d1926] text-[16px] font-medium hover:bg-[#f6f5f0] transition-colors whitespace-nowrap no-underline"
          >
            お問い合わせ
          </a>
        </motion.div>

        {/* Cost comparison */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-20 text-center"
        >
          <h3 className="text-[28px] md:text-[40px] font-extrabold">
            費用対効果
          </h3>
          <p className="mt-3 text-[16px] text-[#1d1926]/60 max-w-lg mx-auto">
            月に3〜4本以上の制作業務がある場合、ほぼ確実にROIが合います。
          </p>
        </motion.div>

        <div className="mt-10 overflow-x-auto">
          <table className="w-full min-w-[600px]">
            <thead>
              <tr className="border-b border-[rgba(29,25,38,0.08)]">
                <th className="text-left text-[14px] text-[#1d1926]/40 font-medium pb-4 pr-8">
                  制作量
                </th>
                <th className="text-left text-[14px] text-[#1d1926]/40 font-medium pb-4 pr-8">
                  外注の場合
                </th>
                <th className="text-left text-[14px] text-[#3eb298] font-medium pb-4">
                  Taskyの場合
                </th>
              </tr>
            </thead>
            <tbody className="text-[16px]">
              {costComparison.map((row) => (
                <tr
                  key={row.volume}
                  className="border-b border-[rgba(29,25,38,0.08)]"
                >
                  <td className="py-5 pr-8 font-medium">{row.volume}</td>
                  <td className="py-5 pr-8 text-[#1d1926]/60">
                    {row.outsource}
                  </td>
                  <td className="py-5 font-bold text-[#3eb298]">
                    {row.tasky}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
